// contexts/AuthContext.tsx - GÜNCELLENMİŞ
import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { authAPI } from '../services/api';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string, signal?: AbortSignal) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
  hasPermission: (requiredRole: string | string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Sadece bir kere çalışacak auth kontrolü
  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.log('🔐 No token found');
          setLoading(false);
          return;
        }

        console.log('🔄 Checking authentication...');
        const response = await authAPI.verify();
        console.log('✅ Auth check successful:', response.data.user);
        setUser(response.data.user);
      } catch (error) {
        console.error('❌ Auth check failed:', error);
        localStorage.removeItem('token');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []); // Boş dependency array - sadece mount'ta çalışsın

  const login = async (email: string, password: string, signal?: AbortSignal): Promise<boolean> => {
    try {
      console.log('🔐 Attempting login...', email);
      
      // Timeout ile login işlemi
      const timeoutDuration = 8000; // 8 saniye
      
      const loginPromise = authAPI.login(email, password);
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Login timeout')), timeoutDuration);
      });

      // Race condition: timeout vs login
      const response = await Promise.race([loginPromise, timeoutPromise]);
      
      const { token, user } = response.data;
      
      console.log('🎉 Login successful! User:', user.name);
      console.log('🔑 User role:', user.role);
      
      localStorage.setItem('token', token);
      setUser(user);
      return true;
    } catch (error: any) {
      console.error('❌ Login error:', error.response?.data || error.message);
      
      // Timeout hatası için özel mesaj
      if (error.message === 'Login timeout') {
        console.error('⏰ Login request timed out');
      }
      
      // Abort hatası için
      if (error.name === 'AbortError') {
        console.log('🛑 Login request aborted');
        throw error; // Yeniden fırlat ki Login component'i yakalayabilsin
      }
      
      localStorage.removeItem('token');
      setUser(null);
      return false;
    }
  };

  const logout = () => {
    console.log('🚪 Logging out...');
    localStorage.removeItem('token');
    setUser(null);
  };

  const hasPermission = (requiredRole: string | string[]) => {
    if (!user) {
      return false;
    }
    
    // Admin her şeyi yapabilir
    if (user.role === 'admin') {
      return true;
    }

    // Array destek
    if (Array.isArray(requiredRole)) {
      return requiredRole.includes(user.role);
    }
    
    // Single role destek
    return user.role === requiredRole;
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      isAuthenticated, 
      loading,
      hasPermission 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};